import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    FaCalendarAlt,
    FaUsers as FaUsersIcon,
    FaMars,
    FaVenus,
    FaChartLine,
    FaRegCalendarCheck,
    FaArrowRight,
    FaSearch,
    FaRegClock,
    FaInfoCircle
} from "react-icons/fa";
import {
    ResponsiveContainer,
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    BarChart,
    Bar
} from 'recharts';
import { getFollowUpCounts, getFollowUpPatients } from '../../api/admin/dashboard';
import CustomDatePicker from '../resuableComponents/CustomDatePicker';
import dayjs from 'dayjs';
import './FollowUpPatients.css';

const FollowUpPatients = () => {
    const navigate = useNavigate();
    const [selectedGender, setSelectedGender] = useState('all');
    const [selectedDate, setSelectedDate] = useState(dayjs());
    const [counts, setCounts] = useState(null);
    const [patients, setPatients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [patientsLoading, setPatientsLoading] = useState(false);
    const [comparisonMonth, setComparisonMonth] = useState([]);
    const [comparisonYear, setComparisonYear] = useState([]);
    const [trendFilter, setTrendFilter] = useState('monthly'); // 'yearly', 'monthly', 'weekly'
    const [selectedObservation, setSelectedObservation] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [listMode, setListMode] = useState('daily'); // 'daily', 'upcoming'
    const [upcomingPatients, setUpcomingPatients] = useState([]);
    const [upcomingLoading, setUpcomingLoading] = useState(false);
    const [error, setError] = useState(null);

    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

    useEffect(() => {
        const fetchCounts = async () => {
            setLoading(true);
            try {
                const [allRes, maleRes, femaleRes] = await Promise.all([
                    getFollowUpCounts(selectedGender),
                    selectedGender === 'all' ? getFollowUpCounts('male') : null,
                    selectedGender === 'all' ? getFollowUpCounts('female') : null
                ]);

                if (allRes.data?.success) {
                    setCounts(allRes.data.data);
                }

                if (selectedGender === 'all' && maleRes?.data?.success && femaleRes?.data?.success) {
                    const mData = maleRes.data.data;
                    const fData = femaleRes.data.data;

                    // Merge Monthly
                    const mMonth = mData.monthWise || [];
                    const fMonth = fData.monthWise || [];
                    const allMonths = new Map();
                    mMonth.forEach(m => allMonths.set(`${m._id.year}-${m._id.month}`, { _id: m._id, male: m.count, female: 0 }));
                    fMonth.forEach(f => {
                        const key = `${f._id.year}-${f._id.month}`;
                        if (allMonths.has(key)) allMonths.get(key).female = f.count;
                        else allMonths.set(key, { _id: f._id, male: 0, female: f.count });
                    });
                    setComparisonMonth(Array.from(allMonths.values())
                        .sort((a, b) => (a._id.year !== b._id.year ? a._id.year - b._id.year : a._id.month - b._id.month))
                        .map(d => ({ name: `${monthNames[d._id.month - 1]} ${d._id.year.toString().slice(2)}`, male: d.male, female: d.female })));

                    // Merge Yearly
                    const mYear = mData.yearWise || [];
                    const fYear = fData.yearWise || [];
                    const allYears = new Map();
                    mYear.forEach(m => allYears.set(m._id.toString(), { _id: m._id, male: m.count, female: 0 }));
                    fYear.forEach(f => {
                        const key = f._id.toString();
                        if (allYears.has(key)) allYears.get(key).female = f.count;
                        else allYears.set(key, { _id: f._id, male: 0, female: f.count });
                    });
                    setComparisonYear(Array.from(allYears.values())
                        .sort((a, b) => a._id - b._id)
                        .map(d => ({ name: d._id.toString(), male: d.male, female: d.female })));
                } else {
                    setComparisonMonth([]);
                    setComparisonYear([]);
                }
            } catch (err) {
                console.error("Failed to fetch follow-up counts:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchCounts();
    }, [selectedGender]);

    useEffect(() => {
        const fetchPatients = async () => {
            setPatientsLoading(true);
            try {
                const formattedDate = selectedDate.format('YYYY-MM-DD');
                const res = await getFollowUpPatients(formattedDate);
                if (res.data?.success) {
                    setPatients(res.data.data);
                    setError(null);
                }
            } catch (err) {
                console.error("Failed to fetch follow-up patients:", err);
                setError("Failed to load follow-up patients. Please try again.");
            } finally {
                setPatientsLoading(false);
            }
        };
        fetchPatients();
    }, [selectedDate]);

    useEffect(() => {
        const fetchUpcoming = async () => {
            if (listMode !== 'upcoming') return;
            setUpcomingLoading(true);
            try {
                const today = new Date();
                const dates = Array.from({ length: 7 }, (_, i) => {
                    const d = new Date(today);
                    d.setDate(today.getDate() + i);
                    return d.toISOString().split('T')[0];
                });

                const responses = await Promise.all(dates.map(date => getFollowUpPatients(date)));
                const allUpcoming = [];
                responses.forEach(res => {
                    if (res.data?.success) {
                        allUpcoming.push(...res.data.data);
                    }
                });
                // Sort by date
                allUpcoming.sort((a, b) => new Date(a.followUpDate) - new Date(b.followUpDate));
                setUpcomingPatients(allUpcoming);
                setError(null);
            } catch (err) {
                console.error("Failed to fetch upcoming follow-ups:", err);
                setError("Failed to load upcoming follow-ups. Please try again.");
            } finally {
                setUpcomingLoading(false);
            }
        };
        fetchUpcoming();
    }, [listMode]);

    // Data formatting for charts
    const formattedMonthData = useMemo(() => {
        if (!counts?.monthWise) return [];
        return counts.monthWise.map(item => ({
            name: `${monthNames[item._id.month - 1]} ${item._id.year.toString().slice(2)}`,
            count: item.count
        }));
    }, [counts]);

    const formattedYearData = useMemo(() => {
        if (!counts?.yearWise) return [];
        return counts.yearWise.map(item => ({
            name: item._id.toString(),
            count: item.count
        }));
    }, [counts]);

    const handleViewObservation = (item) => {
        setSelectedObservation(item);
        setShowModal(true);
    };

    const ObservationModal = () => {
        if (!showModal || !selectedObservation) return null;

        const obs = selectedObservation.observations || {};
        const patient = selectedObservation.patient || {};

        const handleViewPatient = () => {
            if (patient._id) {
                navigate(`/app/patient/user-info/${patient._id}`);
                setShowModal(false);
            }
        };

        return (
            <div className="modal-overlay" onClick={() => setShowModal(false)}>
                <div className="modal-content animate-slide-up" onClick={e => e.stopPropagation()}>
                    <header className="modal-header">
                        <div>
                            <h3>Observation Details</h3>
                            <p>{patient.name} • {selectedObservation.followUpDate ? new Date(selectedObservation.followUpDate).toLocaleDateString() : 'N/A'}</p>
                        </div>
                        <button className="close-modal-btn" onClick={() => setShowModal(false)}>&times;</button>
                    </header>
                    <div className="modal-body">
                        <div className="observation-grid">
                            <div className="obs-item">
                                <span className="obs-label">Visit ID</span>
                                <span className="obs-value">{selectedObservation.visitId || 'N/A'}</span>
                            </div>
                            <div className="obs-item">
                                <span className="obs-label">Appointment ID</span>
                                <span className="obs-value">{selectedObservation.appointmentId || 'N/A'}</span>
                            </div>
                        </div>
                        <div className="observation-grid">
                            <div className="obs-item">
                                <span className="obs-label">Blood Pressure</span>
                                <span className="obs-value">{obs.bp || 'Not recorded'}</span>
                            </div>
                            <div className="obs-item">
                                <span className="obs-label">Pulse</span>
                                <span className="obs-value">{obs.pulse || 'Not recorded'} bpm</span>
                            </div>
                            <div className="obs-item">
                                <span className="obs-label">Weight</span>
                                <span className="obs-value">{obs.weight || 'Not recorded'} kg</span>
                            </div>
                            <div className="obs-item">
                                <span className="obs-label">Temperature</span>
                                <span className="obs-value">{obs.temperature || 'Not recorded'} °F</span>
                            </div>
                            <div className="obs-item">
                                <span className="obs-label">BSL</span>
                                <span className="obs-value">{obs.bsl || 'Not recorded'}</span>
                            </div>
                        </div>
                        <div className="observation-grid">
                            <div className="obs-item">
                                <span className="obs-label">Diagnosis</span>
                                <span className="obs-value">{selectedObservation.diagnosis || 'Not recorded'}</span>
                            </div>
                        </div>
                        <div className="notes-section">
                            <span className="obs-label">Clinical Notes</span>
                            <div className="notes-box">
                                {selectedObservation.notes || "No clinical notes provided for this follow-up."}
                            </div>
                        </div>
                    </div>
                    <footer className="modal-footer">
                        {patient._id && (
                            <button className="btn-primary" onClick={handleViewPatient}>
                                View Patient Record
                            </button>
                        )}
                        <button className="btn-secondary" onClick={() => setShowModal(false)}>Close</button>
                    </footer>
                </div>
            </div>
        );
    };

    return (
        <div className="admin-dashboard follow-up-page">
            <div className="admin-dashboard-frame">
                {/* Hero Section */}
                <section className="admin-dashboard-hero">
                    <div className="hero-content">
                        <h1>Follow-up Patients</h1>
                        <p>Track longitudinal patient care and scheduled follow-ups across different time periods.</p>
                    </div>
                </section>

                {/* Error Banner */}
                {error && (
                    <div className="error-banner" style={{
                        padding: '1rem',
                        marginBottom: '1.5rem',
                        backgroundColor: '#fee2e2',
                        border: '1px solid #fecaca',
                        borderRadius: '8px',
                        color: '#991b1b',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <span>{error}</span>
                        <button
                            onClick={() => setError(null)}
                            style={{
                                background: 'none',
                                border: 'none',
                                color: '#991b1b',
                                fontSize: '1.5rem',
                                cursor: 'pointer',
                                padding: '0'
                            }}
                        >
                            ×
                        </button>
                    </div>
                )}

                {/* Patient List Section */}
                <section className="dashboard-section patient-list-section" style={{ marginBottom: '2rem' }}>
                    <header className="analysis-header" style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div className="analysis-title">
                            <div className="title-icon" style={{ background: '#e0e7ff', color: '#4f46e5' }}>
                                <FaRegCalendarCheck />
                            </div>
                            <div>
                                <h2>Follow-up Schedule</h2>
                                <p>{listMode === 'daily' ? 'View patients for a specific day' : 'Patients scheduled for the next 7 days'}</p>
                            </div>
                        </div>
                        <div className="analysis-filters" style={{ display: 'flex', gap: '1.5rem', alignItems: 'center' }}>
                            <div className="finance-sub-navigation" style={{ margin: 0, padding: '0.2rem', background: '#f1f5f9', display: 'flex' }}>
                                <button
                                    className={`sub-nav-btn ${listMode === 'daily' ? 'active' : ''}`}
                                    onClick={() => setListMode('daily')}
                                    style={{ fontSize: '0.8rem', padding: '0.35rem 1rem' }}
                                >
                                    Specific Date
                                </button>
                                <button
                                    className={`sub-nav-btn ${listMode === 'upcoming' ? 'active' : ''}`}
                                    onClick={() => setListMode('upcoming')}
                                    style={{ fontSize: '0.8rem', padding: '0.35rem 1rem' }}
                                >
                                    Coming 7 Days
                                </button>
                            </div>

                            {listMode === 'daily' && (
                                <div className="date-picker-wrapper">
                                    <CustomDatePicker
                                        value={selectedDate}
                                        onChange={(date) => setSelectedDate(date || dayjs())}
                                        placeholder="Select date"
                                    />
                                </div>
                            )}
                        </div>
                    </header>

                    <div className="patients-grid-container">
                        {(patientsLoading || upcomingLoading) ? (
                            <div className="list-loading-state">
                                <FaRegClock className="animate-spin" />
                                <span>Synchronizing follow-up data...</span>
                            </div>
                        ) : (listMode === 'daily' ? patients : upcomingPatients).length > 0 ? (
                            <div className="patients-list-table-wrapper">
                                <table className="patients-table">
                                    <thead>
                                        <tr>
                                            {listMode === 'upcoming' && <th>Date</th>}
                                            <th>Patient Name</th>
                                            <th>Mobile</th>
                                            <th>Doctor Name</th>
                                            <th style={{ textAlign: 'center' }}>Detail</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(listMode === 'daily' ? patients : upcomingPatients).map((item, idx) => {
                                            const patient = item.patient || {};
                                            return (
                                                <tr key={idx}>
                                                    {listMode === 'upcoming' && (
                                                        <td style={{ fontWeight: 600, color: '#6366f1', fontSize: '0.85rem' }}>
                                                            {new Date(item.followUpDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                                                        </td>
                                                    )}
                                                    <td>
                                                        <div className="patient-info-cell">
                                                            <div className="patient-avatar">
                                                                {patient.name?.charAt(0) || 'P'}
                                                            </div>
                                                            <span className="patient-name">{patient.name}</span>
                                                        </div>
                                                    </td>
                                                    <td>{patient.contact || '-'}</td>
                                                    <td>
                                                        <span style={{ fontWeight: 600 }}>{item.doctor?.name || '-'}</span>
                                                    </td>
                                                    <td style={{ textAlign: 'center' }}>
                                                        <button
                                                            className="info-icon-btn"
                                                            onClick={() => handleViewObservation(item)}
                                                            title="View Observations"
                                                            style={{
                                                                background: '#e0e7ff',
                                                                color: '#4f46e5',
                                                                border: 'none',
                                                                borderRadius: '8px',
                                                                width: '36px',
                                                                height: '36px',
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                cursor: 'pointer',
                                                                transition: 'all 0.2s ease',
                                                                fontSize: '1.2rem'
                                                            }}
                                                        >
                                                            <FaInfoCircle />
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="empty-state">
                                <div className="empty-icon-circle">
                                    <FaSearch />
                                </div>
                                <h3>No Follow-ups Found</h3>
                                <p>
                                    {listMode === 'daily'
                                        ? `There are no scheduled follow-up patients for ${selectedDate.format('DD MMMM YYYY')}.`
                                        : "There are no scheduled follow-up patients for the coming 7 days."}
                                </p>
                                <p className="hint">
                                    {listMode === 'daily' ? "Try selecting a different date from the calendar." : "Scheduled follow-ups will appear here automatically."}
                                </p>
                            </div>
                        )}
                    </div>
                </section>

                <div className="hero-actions" style={{ marginBottom: '2rem' }}>
                    <div className="finance-sub-navigation" style={{ margin: 0, padding: '0.25rem', justifyContent: 'flex-start' }}>
                        <button
                            className={`sub-nav-btn ${selectedGender === 'all' ? 'active' : ''}`}
                            onClick={() => setSelectedGender('all')}
                            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                        >
                            <FaUsersIcon /> All
                        </button>
                        <button
                            className={`sub-nav-btn ${selectedGender === 'male' ? 'active' : ''}`}
                            onClick={() => setSelectedGender('male')}
                            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                        >
                            <FaMars style={{ color: selectedGender === 'male' ? '#3b82f6' : 'inherit' }} /> Male
                        </button>
                        <button
                            className={`sub-nav-btn ${selectedGender === 'female' ? 'active' : ''}`}
                            onClick={() => setSelectedGender('female')}
                            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                        >
                            <FaVenus style={{ color: selectedGender === 'female' ? '#ec4899' : 'inherit' }} /> Female
                        </button>
                    </div>
                </div>

                <div className="dashboard-grid full-width-grid" style={{ display: 'block', marginBottom: '2rem' }}>
                    {/* Follow-up Trends Analytics */}
                    <section className="dashboard-section main-chart full-width-chart" style={{ width: '100%' }}>
                        <header className="content-header-mini" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                                <h3>Follow-up Trends</h3>
                                <p>Visualizing patient retention and follow-up volume</p>
                            </div>
                            <div className="finance-sub-navigation" style={{ margin: 0, padding: '0.25rem', background: '#f8fafc', border: '1px solid #e2e8f0', display: 'flex' }}>
                                <button
                                    className={`sub-nav-btn ${trendFilter === 'monthly' ? 'active' : ''}`}
                                    onClick={() => setTrendFilter('monthly')}
                                    style={{ fontSize: '0.75rem', padding: '0.4rem 1rem' }}
                                >
                                    Monthly
                                </button>
                                <button
                                    className={`sub-nav-btn ${trendFilter === 'yearly' ? 'active' : ''}`}
                                    onClick={() => setTrendFilter('yearly')}
                                    style={{ fontSize: '0.75rem', padding: '0.4rem 1rem' }}
                                >
                                    Yearly
                                </button>
                            </div>
                        </header>
                        <div className="chart-container" style={{ height: 400 }}>
                            {loading ? <div className="skeleton-loader chart-placeholder" /> : (
                                <ResponsiveContainer width="100%" height="100%">
                                    <AreaChart
                                        data={
                                            selectedGender === 'all'
                                                ? (trendFilter === 'yearly' ? comparisonYear : comparisonMonth)
                                                : (trendFilter === 'yearly' ? formattedYearData : formattedMonthData)
                                        }
                                        margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                                    >
                                        <defs>
                                            <linearGradient id="colorFollow" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#4f46e5" stopOpacity={0.2} /><stop offset="95%" stopColor="#4f46e5" stopOpacity={0} /></linearGradient>
                                            <linearGradient id="colorMale" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#3b82f6" stopOpacity={0.1} /><stop offset="95%" stopColor="#3b82f6" stopOpacity={0} /></linearGradient>
                                            <linearGradient id="colorFemale" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#ec4899" stopOpacity={0.1} /><stop offset="95%" stopColor="#ec4899" stopOpacity={0} /></linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                        <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12 }} dy={10} />
                                        <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12 }} />
                                        <Tooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }} />
                                        <Legend verticalAlign="top" height={36} align="right" />
                                        {selectedGender === 'all' ? (
                                            <>
                                                <Area type="monotone" dataKey="male" name="Male" stroke="#3b82f6" strokeWidth={3} fillOpacity={1} fill="url(#colorMale)" animationDuration={1500} />
                                                <Area type="monotone" dataKey="female" name="Female" stroke="#ec4899" strokeWidth={3} fillOpacity={1} fill="url(#colorFemale)" animationDuration={1500} />
                                            </>
                                        ) : (
                                            <Area
                                                type="monotone"
                                                dataKey="count"
                                                name="Follow-ups"
                                                stroke={selectedGender === 'female' ? '#ec4899' : (selectedGender === 'male' ? '#3b82f6' : '#4f46e5')}
                                                strokeWidth={3}
                                                fillOpacity={1}
                                                fill={selectedGender === 'female' ? 'url(#colorFemale)' : (selectedGender === 'male' ? 'url(#colorMale)' : 'url(#colorFollow)')}
                                                animationDuration={1500}
                                            />
                                        )}
                                    </AreaChart>
                                </ResponsiveContainer>
                            )}
                        </div>
                    </section>
                </div>

                <ObservationModal />
            </div>
        </div>
    );
};

export default FollowUpPatients;
